import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Search, Package, Cpu, Presentation, CheckCircle, XCircle, Clock, TrendingUp, AlertTriangle, Copy, Target } from "lucide-react";
import { toast } from "sonner";
import { turkishCities } from "@/utils/turkishData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Hospitals = () => {
  const [hospitals, setHospitals] = useState([]);
  const [filteredHospitals, setFilteredHospitals] = useState([]);
  const [hospitalStats, setHospitalStats] = useState({});
  const [dealers, setDealers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isProbeDialogOpen, setIsProbeDialogOpen] = useState(false);
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);
  const [editingHospital, setEditingHospital] = useState(null);
  const [selectedHospitalForProbe, setSelectedHospitalForProbe] = useState(null);
  const [selectedHospitalForDemo, setSelectedHospitalForDemo] = useState(null);
  const [probeDeliveries, setProbeDeliveries] = useState([]);
  const [demos, setDemos] = useState([]);
  const [probeProducts, setProbeProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    city: "",
    district: "",
    address: "",
    contact_person: "",
    contact_phone: "",
    contact_email: "",
    monthly_probe_target: 0,
  });
  const [probeFormData, setProbeFormData] = useState({
    probe_type: "",
    quantity: 0,
    unit_price: 0,
    delivery_date: "",
    notes: "",
    dealer_id: "",
    product_id: "",
  });
  const [demoFormData, setDemoFormData] = useState({
    demo_person: "",
    demo_date: "",
    result: "beklemede",
    converted_to_sale: false,
    conversion_date: "",
    notes: "",
  });

  useEffect(() => {
    fetchHospitals();
    fetchDealers();
    fetchProbeProducts();
  }, []);

  useEffect(() => {
    filterHospitals();
  }, [hospitals, searchTerm, filterCity]);

  const fetchHospitals = async () => {
    try {
      const response = await axios.get(`${API}/hospitals`);
      setHospitals(response.data);
      
      // Fetch stats for each hospital
      const statsPromises = response.data.map(hospital => 
        axios.get(`${API}/hospitals/${hospital.id}/stats`)
          .then(res => ({ hospitalId: hospital.id, stats: res.data }))
          .catch(() => ({ hospitalId: hospital.id, stats: { device_count: 0, avg_monthly_probe_usage: 0 } }))
      );
      
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(result => {
        statsMap[result.hospitalId] = result.stats;
      });
      setHospitalStats(statsMap);
    } catch (error) {
      console.error("Error fetching hospitals:", error);
      toast.error("Hastaneler yüklenirken hata oluştu");
    }
  };

  const fetchDealers = async () => {
    try {
      const response = await axios.get(`${API}/dealers`);
      setDealers(response.data);
    } catch (error) {
      console.error("Error fetching dealers:", error);
    }
  };

  const fetchProbeProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?category=prob`);
      setProbeProducts(response.data);
    } catch (error) {
      console.error("Error fetching probe products:", error);
    }
  };

  const filterHospitals = () => {
    let filtered = hospitals;

    if (searchTerm) {
      filtered = filtered.filter(
        (h) =>
          h.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          h.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCity) {
      filtered = filtered.filter((h) => h.city === filterCity);
    }

    setFilteredHospitals(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingHospital) {
        await axios.put(`${API}/hospitals/${editingHospital.id}`, formData);
        toast.success("Hastane güncellendi");
      } else {
        await axios.post(`${API}/hospitals`, formData);
        toast.success("Hastane eklendi");
      }

      fetchHospitals();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving hospital:", error);
      toast.error("Hastane kaydedilirken hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu hastaneyi silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      await axios.delete(`${API}/hospitals/${id}`);
      toast.success("Hastane silindi");
      fetchHospitals();
    } catch (error) {
      console.error("Error deleting hospital:", error);
      toast.error("Hastane silinirken hata oluştu");
    }
  };

  const handleEdit = (hospital) => {
    setEditingHospital(hospital);
    setFormData({
      name: hospital.name,
      city: hospital.city,
      district: hospital.district,
      address: hospital.address,
      contact_person: hospital.contact_person || "",
      contact_phone: hospital.contact_phone || "",
      contact_email: hospital.contact_email || "",
      monthly_probe_target: hospital.monthly_probe_target || 0,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingHospital(null);
    setFormData({
      name: "",
      city: "",
      district: "",
      address: "",
      contact_person: "",
      contact_phone: "",
      contact_email: "",
      monthly_probe_target: 0,
    });
  };

  const handleOpenProbeDialog = async (hospital) => {
    setSelectedHospitalForProbe(hospital);
    setIsProbeDialogOpen(true);
    await fetchProbeDeliveries(hospital.id);
  };

  const handleCloseProbeDialog = () => {
    setIsProbeDialogOpen(false);
    setSelectedHospitalForProbe(null);
    setProbeDeliveries([]);
    setProbeFormData({
      probe_type: "",
      quantity: 0,
      unit_price: 0,
      delivery_date: "",
      notes: "",
      dealer_id: "",
    });
  };

  const fetchProbeDeliveries = async (hospitalId) => {
    try {
      const response = await axios.get(`${API}/probe-deliveries?hospital_id=${hospitalId}`);
      setProbeDeliveries(response.data);
    } catch (error) {
      console.error("Error fetching probe deliveries:", error);
      toast.error("Prob kayıtları yüklenirken hata oluştu");
    }
  };

  const handleProbeSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/probe-deliveries`, {
        hospital_id: selectedHospitalForProbe.id,
        probe_type: probeFormData.probe_type,
        quantity: probeFormData.quantity,
        unit_price: probeFormData.unit_price,
        delivery_date: probeFormData.delivery_date,
        notes: probeFormData.notes,
        dealer_id: probeFormData.dealer_id,
        product_id: probeFormData.product_id || null,
      });
      toast.success("Prob kaydı eklendi");
      await fetchProbeDeliveries(selectedHospitalForProbe.id);
      
      // Refresh hospital stats
      const statsResponse = await axios.get(`${API}/hospitals/${selectedHospitalForProbe.id}/stats`);
      setHospitalStats(prev => ({
        ...prev,
        [selectedHospitalForProbe.id]: statsResponse.data
      }));
      
      setProbeFormData({
        probe_type: "",
        quantity: 0,
        unit_price: 0,
        delivery_date: "",
        notes: "",
        dealer_id: "",
        product_id: "",
      });
    } catch (error) {
      console.error("Error adding probe delivery:", error);
      toast.error("Prob kaydı eklenirken hata oluştu");
    }
  };

  const handleDeleteProbeDelivery = async (deliveryId) => {
    if (!window.confirm("Bu prob kaydını silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      await axios.delete(`${API}/probe-deliveries/${deliveryId}`);
      toast.success("Prob kaydı silindi");
      await fetchProbeDeliveries(selectedHospitalForProbe.id);
      
      // Refresh hospital stats
      const statsResponse = await axios.get(`${API}/hospitals/${selectedHospitalForProbe.id}/stats`);
      setHospitalStats(prev => ({
        ...prev,
        [selectedHospitalForProbe.id]: statsResponse.data
      }));
    } catch (error) {
      console.error("Error deleting probe delivery:", error);
      toast.error("Prob kaydı silinirken hata oluştu");
    }
  };

  // Demo Functions
  const handleOpenDemoDialog = async (hospital) => {
    setSelectedHospitalForDemo(hospital);
    setIsDemoDialogOpen(true);
    await fetchDemos(hospital.id);
  };

  const handleCloseDemoDialog = () => {
    setIsDemoDialogOpen(false);
    setSelectedHospitalForDemo(null);
    setDemos([]);
    setDemoFormData({
      demo_person: "",
      demo_date: "",
      result: "beklemede",
      converted_to_sale: false,
      conversion_date: "",
      notes: "",
    });
  };

  const fetchDemos = async (hospitalId) => {
    try {
      const response = await axios.get(`${API}/demos?hospital_id=${hospitalId}`);
      setDemos(response.data);
    } catch (error) {
      console.error("Error fetching demos:", error);
      toast.error("Demo kayıtları yüklenirken hata oluştu");
    }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/demos`, {
        hospital_id: selectedHospitalForDemo.id,
        ...demoFormData,
      });
      toast.success("Demo kaydı eklendi");
      await fetchDemos(selectedHospitalForDemo.id);
      setDemoFormData({
        demo_person: "",
        demo_date: "",
        result: "beklemede",
        converted_to_sale: false,
        conversion_date: "",
        notes: "",
      });
    } catch (error) {
      console.error("Error adding demo:", error);
      toast.error("Demo kaydı eklenirken hata oluştu");
    }
  };

  const handleUpdateDemoConversion = async (demoId, converted) => {
    try {
      await axios.put(`${API}/demos/${demoId}`, {
        converted_to_sale: converted,
        conversion_date: converted ? new Date().toISOString().split('T')[0] : null
      });
      toast.success(converted ? "Satışa dönüştürüldü!" : "Satış durumu güncellendi");
      await fetchDemos(selectedHospitalForDemo.id);
    } catch (error) {
      console.error("Error updating demo:", error);
      toast.error("Demo güncellenirken hata oluştu");
    }
  };

  const handleDeleteDemo = async (demoId) => {
    if (!window.confirm("Bu demo kaydını silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      await axios.delete(`${API}/demos/${demoId}`);
      toast.success("Demo kaydı silindi");
      await fetchDemos(selectedHospitalForDemo.id);
    } catch (error) {
      console.error("Error deleting demo:", error);
      toast.error("Demo kaydı silinirken hata oluştu");
    }
  };

  const getResultBadge = (result) => {
    switch (result) {
      case "olumlu":
        return <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />Olumlu</span>;
      case "olumsuz":
        return <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-red-700"><XCircle className="w-3 h-3" />Olumsuz</span>;
      default:
        return <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3" />Beklemede</span>;
    }
  };

  const getTotalProbesForHospital = (hospitalId) => {
    const hospitalProbes = probeDeliveries.filter(p => p.hospital_id === hospitalId);
    return hospitalProbes.reduce((sum, p) => sum + p.quantity, 0);
  };

  const selectedCityData = turkishCities.find((c) => c.name === formData.city);
  const districts = selectedCityData ? selectedCityData.districts : [];

  const generateAlertMessage = (hospital) => {
    const stats = hospitalStats[hospital.id] || {};
    const target = hospital.monthly_probe_target || 0;
    const avg = stats.avg_monthly_probe_usage || 0;
    const deviceCount = stats.device_count || 0;
    
    // Find last delivery date from stats or deliveries
    const lastOrderDate = stats.last_order_date || "Kayıt yok";
    const formattedLastOrder = lastOrderDate !== "Kayıt yok" 
      ? new Date(lastOrderDate).toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" })
      : "Kayıt yok";

    return `PROB KULLANIM UYARISI

Hastane: ${hospital.name}
Şehir/İlçe: ${hospital.city} / ${hospital.district}
Cihaz Sayısı: ${deviceCount}
Aylık Hedef Prob: ${target} adet
Son 3 Aylık Ortalama Kullanım: ${avg} adet/ay
Son Sipariş Tarihi: ${formattedLastOrder}

${hospital.name} hedeflenen aylık prob kullanım ortalamasının altında sipariş vermektedir. Saha ziyareti planlanmalı, ilgili hekimlerle görüşülerek kullanım düşüşünün sebebi araştırılmalıdır (cihaz arızası, hekim değişikliği, alternatif ürün kullanımı vb.).

Lütfen en kısa sürede ilgili hastaneye ziyaret planlanarak durum değerlendirilsin.`;
  };

  const copyAlertMessage = async (hospital) => {
    const message = generateAlertMessage(hospital);
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Uyarı mesajı kopyalandı!");
    } catch {
      // Fallback
      const textArea = document.createElement("textarea");
      textArea.value = message;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Uyarı mesajı kopyalandı!");
    }
  };

  const isHospitalBelowTarget = (hospital) => {
    const target = hospital.monthly_probe_target || 0;
    if (target <= 0) return false;
    const avg = hospitalStats[hospital.id]?.avg_monthly_probe_usage || 0;
    return avg < target;
  };

  return (
    <div className="space-y-6" data-testid="hospitals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="hospitals-title">
            Hastaneler
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            {filteredHospitals.length} hastane kayıtlı
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-hospital-btn" onClick={() => setEditingHospital(null)}>
              <Plus className="w-4 h-4 mr-2" />
              Yeni Hastane
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hospital-dialog">
            <DialogHeader>
              <DialogTitle>
                {editingHospital ? "Hastane Düzenle" : "Yeni Hastane Ekle"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Hastane Adı *</Label>
                  <Input
                    id="name"
                    data-testid="hospital-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="city">Şehir *</Label>
                  <Select
                    value={formData.city}
                    onValueChange={(value) => setFormData({ ...formData, city: value, district: "" })}
                    required
                  >
                    <SelectTrigger data-testid="hospital-city-select">
                      <SelectValue placeholder="Şehir seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {turkishCities.map((city) => (
                        <SelectItem key={city.name} value={city.name}>
                          {city.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="district">İlçe *</Label>
                  <Select
                    value={formData.district}
                    onValueChange={(value) => setFormData({ ...formData, district: value })}
                    disabled={!formData.city}
                    required
                  >
                    <SelectTrigger data-testid="hospital-district-select">
                      <SelectValue placeholder="İlçe seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {districts.map((district) => (
                        <SelectItem key={district} value={district}>
                          {district}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address">Adres *</Label>
                  <Input
                    id="address"
                    data-testid="hospital-address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="contact_person">İletişim Kişisi</Label>
                  <Input
                    id="contact_person"
                    data-testid="hospital-contact-person-input"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone">Telefon</Label>
                  <Input
                    id="contact_phone"
                    data-testid="hospital-contact-phone-input"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">E-posta</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    data-testid="hospital-contact-email-input"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="monthly_probe_target" className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-orange-500" />
                    Aylık Hedef Prob Sayısı
                  </Label>
                  <Input
                    id="monthly_probe_target"
                    type="number"
                    min="0"
                    data-testid="hospital-probe-target-input"
                    placeholder="Ör: 20 (0 = uyarı devre dışı)"
                    value={formData.monthly_probe_target}
                    onChange={(e) => setFormData({ ...formData, monthly_probe_target: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    3 aylık ortalama kullanım bu değerin altına düşerse uyarı verilir
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  data-testid="cancel-hospital-btn"
                >
                  İptal
                </Button>
                <Button type="submit" data-testid="save-hospital-btn">
                  {editingHospital ? "Güncelle" : "Kaydet"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Hastane adı veya adres ara..."
                  data-testid="search-hospital-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-64">
              <Select value={filterCity || undefined} onValueChange={(value) => setFilterCity(value === "all" ? "" : value)}>
                <SelectTrigger data-testid="filter-city-select">
                  <SelectValue placeholder="Şehir filtrele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Şehirler</SelectItem>
                  {turkishCities.map((city) => (
                    <SelectItem key={city.name} value={city.name}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hospitals Table */}
      <Card>
        <CardHeader>
          <CardTitle>Hastane Listesi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="hospitals-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Hastane Adı</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Şehir</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">İlçe</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Cihaz Sayısı</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Hedef / Ort. Kullanım</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">İletişim Kişisi</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Prob Takibi</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredHospitals.map((hospital) => {
                  const belowTarget = isHospitalBelowTarget(hospital);
                  const stats = hospitalStats[hospital.id] || {};
                  return (
                  <tr
                    key={hospital.id}
                    className={`border-b border-border table-row ${belowTarget ? 'bg-red-50' : ''}`}
                    data-testid={`hospital-row-${hospital.id}`}
                  >
                    <td className="py-3 px-4 font-medium">
                      <div className="flex items-center gap-2">
                        {belowTarget && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                        {hospital.name}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{hospital.city}</td>
                    <td className="py-3 px-4 text-muted-foreground">{hospital.district}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Cpu className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-green-600">
                          {stats.device_count || 0}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {hospital.monthly_probe_target > 0 ? (
                          <>
                            <div className="flex items-center gap-2">
                              <span className={`text-lg font-bold ${belowTarget ? 'text-red-600' : 'text-green-600'}`}>
                                {stats.avg_monthly_probe_usage || 0}
                              </span>
                              <span className="text-xs text-muted-foreground">/</span>
                              <span className="text-sm font-medium text-slate-500">
                                {hospital.monthly_probe_target}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">ort. / hedef</span>
                            {belowTarget && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyAlertMessage(hospital)}
                                data-testid={`copy-alert-${hospital.id}`}
                                className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-100 mt-1"
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                <span className="text-xs">Uyarı Mesajı</span>
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="font-semibold text-purple-600">
                              {stats.avg_monthly_probe_usage || 0}
                            </span>
                            <span className="text-xs text-muted-foreground">prob/ay</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {hospital.contact_person || "-"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenProbeDialog(hospital)}
                          data-testid={`probe-tracking-${hospital.id}`}
                        >
                          <Package className="w-4 h-4 mr-2" />
                          Prob Takibi
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDemoDialog(hospital)}
                          data-testid={`demo-tracking-${hospital.id}`}
                          className="border-orange-300 text-orange-600 hover:bg-orange-50"
                        >
                          <Presentation className="w-4 h-4 mr-2" />
                          Demo Takibi
                        </Button>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(hospital)}
                          data-testid={`edit-hospital-${hospital.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(hospital.id)}
                          data-testid={`delete-hospital-${hospital.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredHospitals.length === 0 && (
              <div className="text-center py-12" data-testid="no-hospitals">
                <p className="text-muted-foreground">Hastane bulunamadı</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Probe Delivery Dialog */}
      <Dialog open={isProbeDialogOpen} onOpenChange={setIsProbeDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="probe-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedHospitalForProbe?.name} - Prob Takibi
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Add Probe Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Yeni Prob Kaydı Ekle</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProbeSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Envanterden Ürün Seçin</Label>
                      <Select value={probeFormData.product_id} onValueChange={(value) => {
                        if (value === "manual") {
                          setProbeFormData({ ...probeFormData, product_id: "", probe_type: "", unit_price: 0 });
                        } else {
                          const product = probeProducts.find(p => p.id === value);
                          if (product) {
                            setProbeFormData({ ...probeFormData, product_id: product.id, probe_type: product.name, unit_price: product.unit_price });
                          }
                        }
                      }}>
                        <SelectTrigger data-testid="probe-product-select">
                          <SelectValue placeholder="Envanterden seçin veya manuel girin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manuel Giriş</SelectItem>
                          {probeProducts.map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name} (Stok: {product.stock_quantity})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {probeFormData.product_id && (
                      <div className="col-span-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm">
                        <span className="text-blue-700 font-medium">Seçilen: {probeFormData.probe_type}</span>
                        <span className="text-blue-600 ml-2">(Stoktan otomatik düşülecek)</span>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="probe_type">Prob Tipi *</Label>
                      <Input
                        id="probe_type"
                        data-testid="probe-type-input"
                        placeholder="Örn: Shuliang Ligasure LF1212"
                        value={probeFormData.probe_type}
                        onChange={(e) => setProbeFormData({ ...probeFormData, probe_type: e.target.value })}
                        required
                        disabled={!!probeFormData.product_id}
                      />
                    </div>
                    <div>
                      <Label htmlFor="quantity">Adet *</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="1"
                        data-testid="probe-quantity-input"
                        value={probeFormData.quantity}
                        onChange={(e) => setProbeFormData({ ...probeFormData, quantity: parseInt(e.target.value) || 0 })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="unit_price">Birim Fiyat (₺) *</Label>
                      <Input
                        id="unit_price"
                        type="number"
                        min="0"
                        step="0.01"
                        data-testid="probe-unit-price-input"
                        placeholder="0.00"
                        value={probeFormData.unit_price}
                        onChange={(e) => setProbeFormData({ ...probeFormData, unit_price: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="delivery_date">Teslim Tarihi *</Label>
                      <Input
                        id="delivery_date"
                        type="date"
                        data-testid="probe-delivery-date-input"
                        value={probeFormData.delivery_date}
                        onChange={(e) => setProbeFormData({ ...probeFormData, delivery_date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="dealer_id">Bayi *</Label>
                      <Select
                        value={probeFormData.dealer_id}
                        onValueChange={(value) => setProbeFormData({ ...probeFormData, dealer_id: value })}
                        required
                      >
                        <SelectTrigger data-testid="probe-dealer-select">
                          <SelectValue placeholder="Bayi seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {dealers.map((dealer) => (
                            <SelectItem key={dealer.id} value={dealer.id}>
                              <span className="flex items-center gap-2">
                                <span 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: dealer.color }}
                                />
                                {dealer.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="notes">Notlar</Label>
                      <Input
                        id="notes"
                        data-testid="probe-notes-input"
                        placeholder="Ek bilgiler..."
                        value={probeFormData.notes}
                        onChange={(e) => setProbeFormData({ ...probeFormData, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" data-testid="add-probe-btn" disabled={!probeFormData.dealer_id}>
                      <Plus className="w-4 h-4 mr-2" />
                      Ekle
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Probe Deliveries List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Prob Geçmişi
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    (Toplam: {probeDeliveries.reduce((sum, p) => sum + p.quantity, 0)} adet)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {probeDeliveries.length === 0 ? (
                  <div className="text-center py-8" data-testid="no-probe-deliveries">
                    <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Henüz prob kaydı bulunmamaktadır</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {probeDeliveries
                      .sort((a, b) => new Date(b.delivery_date) - new Date(a.delivery_date))
                      .map((delivery) => (
                        <div
                          key={delivery.id}
                          className="flex items-center justify-between p-4 bg-secondary rounded-lg"
                          data-testid={`probe-delivery-${delivery.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <Package className="w-5 h-5 text-purple-600" />
                              <div>
                                <p className="font-medium">{delivery.probe_type}</p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(delivery.delivery_date).toLocaleDateString("tr-TR", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  })}
                                </p>
                                <div className="flex gap-2 mt-1">
                                  {delivery.dealer_name && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 font-medium">
                                      Bayi: {delivery.dealer_name}
                                    </span>
                                  )}
                                  {delivery.agent_name && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                      {delivery.agent_name}
                                    </span>
                                  )}
                                </div>
                                {delivery.notes && (
                                  <p className="text-xs text-muted-foreground mt-1">{delivery.notes}</p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-2xl font-bold text-purple-600">{delivery.quantity}</p>
                              <p className="text-xs text-muted-foreground">adet</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProbeDelivery(delivery.id)}
                              data-testid={`delete-probe-${delivery.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={handleCloseProbeDialog} data-testid="close-probe-dialog-btn">
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Demo Tracking Dialog */}
      <Dialog open={isDemoDialogOpen} onOpenChange={setIsDemoDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="demo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Presentation className="w-5 h-5 text-orange-600" />
              {selectedHospitalForDemo?.name} - Demo Takibi
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Demo Add Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Yeni Demo Kaydı Ekle</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleDemoSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="demo_person">Demo Yapan Kişi *</Label>
                      <Input
                        id="demo_person"
                        placeholder="Ad Soyad"
                        data-testid="demo-person-input"
                        value={demoFormData.demo_person}
                        onChange={(e) => setDemoFormData({ ...demoFormData, demo_person: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="demo_date">Demo Tarihi *</Label>
                      <Input
                        id="demo_date"
                        type="date"
                        data-testid="demo-date-input"
                        value={demoFormData.demo_date}
                        onChange={(e) => setDemoFormData({ ...demoFormData, demo_date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="result">Sonuç *</Label>
                      <Select
                        value={demoFormData.result}
                        onValueChange={(value) => setDemoFormData({ ...demoFormData, result: value })}
                      >
                        <SelectTrigger data-testid="demo-result-select">
                          <SelectValue placeholder="Sonuç seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="olumlu">
                            <span className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Olumlu
                            </span>
                          </SelectItem>
                          <SelectItem value="olumsuz">
                            <span className="flex items-center gap-2">
                              <XCircle className="w-4 h-4 text-red-600" />
                              Olumsuz
                            </span>
                          </SelectItem>
                          <SelectItem value="beklemede">
                            <span className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-yellow-600" />
                              Beklemede
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="notes">Notlar</Label>
                      <Input
                        id="notes"
                        placeholder="Demo hakkında notlar..."
                        data-testid="demo-notes-input"
                        value={demoFormData.notes}
                        onChange={(e) => setDemoFormData({ ...demoFormData, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" data-testid="add-demo-btn" className="bg-orange-600 hover:bg-orange-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Demo Ekle
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Demo List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Demo Geçmişi</span>
                  <span className="text-sm font-normal text-muted-foreground">
                    Toplam: {demos.length} demo | 
                    Satışa Dönüşen: {demos.filter(d => d.converted_to_sale).length}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {demos.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Presentation className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Bu hastanede henüz demo kaydı bulunmamaktadır</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {demos
                      .sort((a, b) => new Date(b.demo_date) - new Date(a.demo_date))
                      .map((demo) => (
                        <div
                          key={demo.id}
                          className={`p-4 rounded-lg border ${demo.converted_to_sale ? 'bg-green-50 border-green-200' : 'bg-secondary'}`}
                          data-testid={`demo-${demo.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Presentation className="w-5 h-5 text-orange-600" />
                                <span className="font-medium">{demo.demo_person}</span>
                                {getResultBadge(demo.result)}
                                {demo.converted_to_sale && (
                                  <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-600 text-white">
                                    <TrendingUp className="w-3 h-3" />
                                    Satışa Dönüştü
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {new Date(demo.demo_date).toLocaleDateString("tr-TR", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })}
                                {demo.created_by_name && (
                                  <span className="ml-2 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                    Kaydeden: {demo.created_by_name}
                                  </span>
                                )}
                              </p>
                              {demo.notes && (
                                <p className="text-sm text-muted-foreground mt-2 italic">"{demo.notes}"</p>
                              )}
                              {demo.converted_to_sale && demo.conversion_date && (
                                <p className="text-xs text-green-600 mt-1">
                                  Satış Tarihi: {new Date(demo.conversion_date).toLocaleDateString("tr-TR")}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {!demo.converted_to_sale && demo.result === "olumlu" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUpdateDemoConversion(demo.id, true)}
                                  data-testid={`convert-demo-${demo.id}`}
                                  className="text-green-600 border-green-300 hover:bg-green-50"
                                >
                                  <TrendingUp className="w-4 h-4 mr-1" />
                                  Satışa Dönüştür
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDemo(demo.id)}
                                data-testid={`delete-demo-${demo.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={handleCloseDemoDialog} data-testid="close-demo-dialog-btn">
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Hospitals;